import { useWindowResize } from '@toggle/helpers';
import { getInstanceByDom, init } from 'echarts';
import React, { useEffect, useRef, useState } from 'react';

import { ThemeName } from '~/design-tokens/themes/themes';

import * as S from './PieChart.styles';
import { createPieSeries, getColors } from './utils';

export type PieChartData = {
  value: number;
  name: string;
}[];
export interface PieChartProps {
  data: PieChartData;
  theme?: ThemeName;
}

export const PieChart = ({
  data,
  theme = 'dark_default',
}: PieChartProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { width, height } = useWindowResize({ targetRef: ref });
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const { normalColors } = getColors(theme);

  const onLegendEnter = (i: number) => {
    setActiveIndex(i);

    if (ref.current) {
      const chart = getInstanceByDom(ref.current);
      chart?.dispatchAction({
        type: 'highlight',
        seriesIndex: 0,
        dataIndex: i,
      });
    }
  };

  const onLegendLeave = (i: number) => {
    setActiveIndex(null);

    if (ref.current) {
      const chart = getInstanceByDom(ref.current);
      chart?.dispatchAction({ type: 'downplay', seriesIndex: 0, dataIndex: i });
    }
  };

  useEffect(() => {
    if (ref.current) {
      const chart = getInstanceByDom(ref.current);
      chart?.resize({ width, height });
    }
  }, [width, height]);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const chart = init(ref.current, theme);
    chart.setOption({
      legend: { show: false },
      series: createPieSeries(data, theme),
    });

    chart.on('mouseover', params => {
      if (params.componentType === 'series') {
        setActiveIndex(params.dataIndex);
      }
    });
    chart.on('mouseout', () => {
      setActiveIndex(null);
    });

    return () => {
      chart.dispose();
    };
  }, [data, theme]);

  return (
    <S.Root>
      <S.ChartWrapper ref={ref} />
      <S.LegendWrapper>
        {data.map((item, idx) => (
          <S.Row
            $isActive={activeIndex === idx}
            onMouseEnter={() => onLegendEnter(idx)}
            onMouseLeave={() => onLegendLeave(idx)}
            key={`row-${idx}`}
          >
            <S.LegendNameWrapper>
              <S.StyledDot $color={normalColors[idx]} />
              <S.BodySmallText>{item.name}</S.BodySmallText>
            </S.LegendNameWrapper>
            <S.BodySmallText>
              {(item.value * 100).toFixed(2) + '%'}
            </S.BodySmallText>
          </S.Row>
        ))}
      </S.LegendWrapper>
    </S.Root>
  );
};
